/**
 * Content Analyzer for Visa Interview Preparation
 *
 * Analyzes LinkedIn posts, articles, messages, and other content
 * for potential red flags during visa interviews.
 *
 * IMPORTANT: This analyzer is designed to help identify content that
 * consular officers MIGHT find concerning. It's not making political
 * judgments but flagging content that could complicate visa interviews.
 */

import type {
  ContentAnalysis,
  ContentFlag,
  ContentFlagCategory,
  Severity,
  Article,
  Message,
  Publication,
  Profile,
  Recommendation,
  Cause,
  ContentPatterns,
} from '../types.js';

/**
 * Patterns for content analysis
 * These are based on common concerns raised during visa interviews
 */
const CONTENT_PATTERNS: ContentPatterns = {
  // Content that might be perceived as anti-US sentiment
  antiUS: [
    'american imperialism',
    'us imperialism',
    'american colonialism',
    'death to america',
    'anti-american',
    'hate america',
    'us aggression',
    'american aggression',
    'us terrorism',
    'american terrorism',
    'boycott usa',
    'boycott america',
    'boycott american',
    'us war crimes',
    'american war crimes',
    'us military crimes',
    'down with america',
    'damn america',
    'us enemy',
    'america is evil',
    'us is evil',
    'america is the problem',
    'us is the problem',
    'us occupation',
    'american occupation',
    'us oppression',
    'american oppression',
  ],

  // Political activism that might raise concerns
  politicalActivism: [
    // Extremist indicators
    'overthrow government',
    'revolution against',
    'armed resistance',
    'violent protest',
    'militant action',
    'radical movement',
    'destroy the system',
    'burn it down',
    'by any means necessary',
    // Associations that might be flagged
    'communist party',
    'socialist revolution',
    'anarchist movement',
    'separatist movement',
    // Note: We're NOT flagging legitimate political discourse
  ],

  // Language suggesting permanent immigration intent (problematic for non-immigrant visas)
  immigrationIntent: [
    'never going back',
    'staying forever',
    'permanent home in america',
    'american citizen goal',
    'citizenship journey',
    'path to citizenship',
    'green card journey',
    'settling in america',
    'making america my home',
    'building my life in america',
    'american dream permanent',
    'no plans to return',
    'not returning to',
    'left my country for good',
    'new life in america',
    'america is home now',
    'my home is america',
    'permanent resident goal',
  ],

  // Mentions of sanctioned entities or countries (OFAC concerns)
  sanctionedEntities: [
    // These are general patterns - specific sanctions lists change
    'north korea',
    'dprk',
    'iran revolutionary guard',
    'irgc',
    'hezbollah',
    'hamas',
    'al qaeda',
    'al-qaeda',
    'isis',
    'isil',
    'islamic state',
    'taliban',
    // Note: Simply mentioning these isn't necessarily bad
    // Context matters - news discussion vs. support
  ],

  // Controversial topics that might raise additional scrutiny
  controversialTopics: [
    // These aren't necessarily flags but might prompt extra questions
    'visa fraud',
    'overstay visa',
    'work without authorization',
    'illegal work',
    'undocumented work',
    'cash only work',
    'under the table',
    'fake documents',
    'forged documents',
    'bought diploma',
    'fake degree',
  ],

  // Positive indicators (not flags, but good to identify)
  positiveIndicators: [
    'grateful for opportunity',
    'american innovation',
    'us technology leadership',
    'contributing to america',
    'building bridges',
    'cultural exchange',
    'international collaboration',
    'legal immigration',
    'proper visa process',
    'following regulations',
    'ties to home country',
    'returning home',
    'family back home',
    'property in home country',
  ],
};

/**
 * Analyze all content sources for potential flags
 */
export function analyzeContent(
  articles: Article[],
  messages: Message[],
  publications: Publication[],
  profile: Profile | null,
  _recommendations: Recommendation[],
  causes: Cause[],
  userSentMessages?: Message[]
): ContentAnalysis {
  const flags: ContentFlag[] = [];

  // Analyze articles
  for (const article of articles) {
    flags.push(...analyzeText(
      article.content,
      'article',
      article.title,
      article.publishedOn || article.createdOn
    ));
    flags.push(...analyzeText(
      article.title,
      'article',
      article.title,
      article.publishedOn || article.createdOn
    ));
  }

  // Analyze user's sent messages (not received - those aren't their words)
  const sentMessages = userSentMessages || messages.filter(m =>
    m.senderProfileUrl?.includes('/in/') &&
    profile &&
    (m.from?.toLowerCase().includes(profile.firstName?.toLowerCase()) ||
     m.senderProfileUrl?.toLowerCase().includes(profile.firstName?.toLowerCase()))
  );

  for (const message of sentMessages) {
    // Only analyze substantial messages
    if (message.content && message.content.length > 50) {
      flags.push(...analyzeText(
        message.content,
        'message',
        `Message to ${message.to}`,
        message.date
      ));
    }
  }

  // Analyze publications
  for (const pub of publications) {
    flags.push(...analyzeText(
      pub.description,
      'publication',
      pub.name,
      pub.publishedOn
    ));
    flags.push(...analyzeText(
      pub.name,
      'publication',
      pub.name,
      pub.publishedOn
    ));
  }

  // Analyze profile summary and headline
  if (profile) {
    flags.push(...analyzeText(
      profile.summary,
      'profile',
      'Profile Summary',
      null
    ));
    flags.push(...analyzeText(
      profile.headline,
      'profile',
      'Profile Headline',
      null
    ));
  }

  // Analyze causes (can indicate political leanings)
  for (const cause of causes) {
    const causeFlags = analyzeCause(cause);
    flags.push(...causeFlags);
  }

  // Deduplicate similar flags
  const dedupedFlags = deduplicateFlags(flags);

  // Calculate overall risk
  const overallRisk = calculateContentRisk(dedupedFlags);

  // Generate summary
  const summary = generateContentSummary(dedupedFlags);

  return {
    flaggedItems: dedupedFlags,
    overallRisk,
    summary,
  };
}

/**
 * Analyze text content for flags
 */
function analyzeText(
  text: string | undefined,
  source: ContentFlag['source'],
  _title: string,
  date: Date | null
): ContentFlag[] {
  if (!text || text.trim().length === 0) return [];

  const flags: ContentFlag[] = [];
  const lowerText = text.toLowerCase();

  // Check anti-US sentiment
  for (const pattern of CONTENT_PATTERNS.antiUS) {
    if (lowerText.includes(pattern)) {
      flags.push({
        severity: 'red',
        category: 'anti_us_sentiment',
        source,
        excerpt: extractExcerpt(text, pattern),
        fullContent: text,
        reason: `Content contains phrase "${pattern}" which could be interpreted as anti-US sentiment`,
        recommendation: 'Consider removing or archiving this content before your interview. ' +
                        'Even if taken out of context, such language can create significant complications.',
        date,
      });
      break; // One flag per category per text is enough
    }
  }

  // Check political activism concerns
  for (const pattern of CONTENT_PATTERNS.politicalActivism) {
    if (lowerText.includes(pattern)) {
      flags.push({
        severity: 'red',
        category: 'political_activism',
        source,
        excerpt: extractExcerpt(text, pattern),
        fullContent: text,
        reason: `Content contains phrase "${pattern}" which may indicate concerning political activism`,
        recommendation: 'Political activity, especially that which could be seen as extremist, ' +
                        'can raise security concerns. Review this content carefully.',
        date,
      });
      break;
    }
  }

  // Check immigration intent (for non-immigrant visa holders)
  for (const pattern of CONTENT_PATTERNS.immigrationIntent) {
    if (lowerText.includes(pattern)) {
      flags.push({
        severity: 'orange',
        category: 'immigration_intent_permanent',
        source,
        excerpt: extractExcerpt(text, pattern),
        fullContent: text,
        reason: `Content contains phrase "${pattern}" which may suggest permanent immigration intent`,
        recommendation: 'For non-immigrant visas (H-1B, L-1, etc.), avoid public statements ' +
                        'suggesting intent to stay permanently. Emphasize ties to home country.',
        date,
      });
      break;
    }
  }

  // Check sanctioned entity mentions (context-dependent)
  for (const pattern of CONTENT_PATTERNS.sanctionedEntities) {
    if (lowerText.includes(pattern)) {
      // Check if it's just a news mention vs something more concerning
      const isNewsContext = lowerText.includes('news') ||
                           lowerText.includes('article') ||
                           lowerText.includes('report') ||
                           lowerText.includes('according to');

      const isSupportive = lowerText.includes('support') ||
                          lowerText.includes('agree with') ||
                          lowerText.includes('stand with') ||
                          lowerText.includes('join');

      if (isSupportive && !isNewsContext) {
        flags.push({
          severity: 'red',
          category: 'sanctioned_entity_mention',
          source,
          excerpt: extractExcerpt(text, pattern),
          fullContent: text,
          reason: `Content appears to express support for "${pattern}" which may be a sanctioned entity`,
          recommendation: 'Any appearance of support for sanctioned entities can result in visa denial ' +
                          'or revocation. Remove this content immediately and consult with an attorney if needed.',
          date,
        });
        break;
      } else if (!isNewsContext) {
        flags.push({
          severity: 'orange',
          category: 'sanctioned_entity_mention',
          source,
          excerpt: extractExcerpt(text, pattern),
          fullContent: text,
          reason: `Content mentions "${pattern}" - ensure context is clearly neutral/news-based`,
          recommendation: 'While neutral mentions may be fine, be prepared to explain the context. ' +
                          'Consider if this content is necessary.',
          date,
        });
        break;
      }
    }
  }

  // Check controversial topics
  for (const pattern of CONTENT_PATTERNS.controversialTopics) {
    if (lowerText.includes(pattern)) {
      flags.push({
        severity: 'red',
        category: 'visa_fraud_indicator',
        source,
        excerpt: extractExcerpt(text, pattern),
        fullContent: text,
        reason: `Content contains phrase "${pattern}" which relates to visa/immigration violations`,
        recommendation: 'Content discussing visa violations or fraud, even hypothetically, ' +
                        'should be removed immediately.',
        date,
      });
      break;
    }
  }

  return flags;
}

/**
 * Analyze causes for potential flags
 */
function analyzeCause(cause: Cause): ContentFlag[] {
  const flags: ContentFlag[] = [];
  const causeLower = cause.name.toLowerCase();

  // Generally, most causes are fine. Only flag potentially concerning ones.
  // Most causes are completely fine - human rights, education, etc.
  // Only flag if explicitly problematic

  // Check for sanctioned-related causes
  for (const pattern of CONTENT_PATTERNS.sanctionedEntities) {
    if (causeLower.includes(pattern)) {
      flags.push({
        severity: 'orange',
        category: 'sanctioned_entity_mention',
        source: 'profile',
        excerpt: cause.name,
        fullContent: cause.name,
        reason: `Cause "${cause.name}" may be associated with sanctioned entities`,
        recommendation: 'Review if this cause association could raise questions during your interview.',
        date: null,
      });
    }
  }

  return flags;
}

/**
 * Extract an excerpt around a pattern match
 */
function extractExcerpt(text: string, pattern: string): string {
  const lowerText = text.toLowerCase();
  const index = lowerText.indexOf(pattern.toLowerCase());

  if (index === -1) return text.substring(0, 200);

  const start = Math.max(0, index - 50);
  const end = Math.min(text.length, index + pattern.length + 50);

  let excerpt = text.substring(start, end);
  if (start > 0) excerpt = '...' + excerpt;
  if (end < text.length) excerpt = excerpt + '...';

  return excerpt;
}

/**
 * Deduplicate similar flags
 */
function deduplicateFlags(flags: ContentFlag[]): ContentFlag[] {
  const seen = new Set<string>();
  const result: ContentFlag[] = [];

  for (const flag of flags) {
    // Create a key based on category, source type, and excerpt hash
    const key = `${flag.category}-${flag.source}-${flag.excerpt.substring(0, 50)}`;

    if (!seen.has(key)) {
      seen.add(key);
      result.push(flag);
    }
  }

  return result;
}

/**
 * Calculate overall content risk
 */
function calculateContentRisk(flags: ContentFlag[]): Severity {
  const redCount = flags.filter(f => f.severity === 'red').length;
  const orangeCount = flags.filter(f => f.severity === 'orange').length;

  if (redCount >= 1) return 'red';
  if (orangeCount >= 2) return 'orange';
  return 'green';
}

/**
 * Generate a summary of content analysis
 */
function generateContentSummary(flags: ContentFlag[]): string {
  if (flags.length === 0) {
    return 'No concerning content detected in your LinkedIn data. ' +
           'Your profile appears safe for visa interview purposes.';
  }

  const redFlags = flags.filter(f => f.severity === 'red');
  const orangeFlags = flags.filter(f => f.severity === 'orange');

  const parts: string[] = [];

  if (redFlags.length > 0) {
    const categories = [...new Set(redFlags.map(f => categoryToReadable(f.category)))];
    parts.push(`CRITICAL: ${redFlags.length} high-severity issue(s) found in ${categories.join(', ')}.`);
  }

  if (orangeFlags.length > 0) {
    const categories = [...new Set(orangeFlags.map(f => categoryToReadable(f.category)))];
    parts.push(`WARNING: ${orangeFlags.length} moderate concern(s) found in ${categories.join(', ')}.`);
  }

  parts.push('Review each flagged item and consider the recommendations provided.');

  return parts.join(' ');
}

/**
 * Convert category to human-readable string
 */
function categoryToReadable(category: ContentFlagCategory): string {
  const map: Record<ContentFlagCategory, string> = {
    anti_us_sentiment: 'anti-US sentiment',
    political_activism: 'political activism',
    immigration_intent_permanent: 'permanent immigration intent',
    sanctioned_entity_mention: 'sanctioned entity mentions',
    controversial_opinion: 'controversial opinions',
    visa_fraud_indicator: 'visa-related concerns',
    unauthorized_work: 'unauthorized work references',
    illegal_activity: 'illegal activity references',
    security_concern: 'security concerns',
  };
  return map[category] || category;
}

/**
 * Analyze content for positive indicators (ties to home country, etc.)
 */
export function findPositiveIndicators(
  articles: Article[],
  profile: Profile | null,
  messages: Message[]
): string[] {
  const indicators: string[] = [];

  const allText = [
    ...articles.map(a => a.content),
    profile?.summary || '',
    profile?.headline || '',
    ...messages.map(m => m.content),
  ].join(' ').toLowerCase();

  for (const pattern of CONTENT_PATTERNS.positiveIndicators) {
    if (allText.includes(pattern)) {
      indicators.push(`Found positive indicator: "${pattern}"`);
    }
  }

  // Check for home country ties in profile
  if (profile?.summary) {
    const homeCountryPatterns = [
      'family in', 'property in', 'return to', 'going back to',
      'my country', 'back home', 'home country',
    ];

    for (const pattern of homeCountryPatterns) {
      if (profile.summary.toLowerCase().includes(pattern)) {
        indicators.push(`Profile mentions ties to home country ("${pattern}")`);
        break;
      }
    }
  }

  return indicators;
}
